import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { PresenceComputer, PresenceVpnSession, PresenceWifi } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable } from '../components/DataTable'
import type { Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * La presenza di una persona: dov'è collegata, adesso, per sorgente.
 *
 * La CMDB parla di macchine, ma le domande operative spesso partono da una
 * persona: «è in ufficio o in VPN? da quale postazione?». Wi-Fi e PC sono
 * l'ultima lettura dell'inventario; la VPN si chiede al firewall al momento
 * dell'apertura, perché una sessione VPN vive minuti e una fotografia sarebbe
 * quasi sempre vecchia. Ci si arriva dalla ricerca globale digitando la login.
 */
export default function Presenza() {
  const [params] = useSearchParams()
  const login = (params.get('login') ?? '').trim()
  const dati = useAsync(() => api.presenza(login), [login])
  const d = dati.data

  const colonneWifi: Column<PresenceWifi>[] = [
    { key: 'ap', header: 'AP', value: (r) => r.ap_name ?? '' },
    { key: 'ssid', header: 'SSID', value: (r) => r.ssid ?? '' },
    {
      key: 'mac',
      header: 'MAC',
      mono: true,
      value: (r) => r.mac,
      render: (r) =>
        r.client_ci_id ? <Link to={`/cis/${r.client_ci_id}`}>{r.mac}</Link> : r.mac,
    },
    { key: 'ip', header: t('Indirizzo'), mono: true, value: (r) => r.ip ?? '' },
    { key: 'band', header: t('Banda'), value: (r) => r.band ?? '' },
    { key: 'rssi', header: 'RSSI', mono: true, value: (r) => r.rssi ?? '' },
    {
      key: 'controller',
      header: t('Controller'),
      value: (r) => r.controller_name ?? '',
      render: (r) => (
        <Link to={`/cis/${r.controller_ci_id}#wireless`}>{r.controller_name ?? '—'}</Link>
      ),
    },
  ]
  const colonneVpn: Column<PresenceVpnSession>[] = [
    { key: 'firewall', header: t('Firewall'), value: (r) => r.firewall },
    { key: 'computer', header: t('Postazione'), value: (r) => r.computer ?? '' },
    { key: 'client', header: t('Client'), value: (r) => r.client ?? '' },
    { key: 'virtual_ip', header: t('IP VPN'), mono: true, value: (r) => r.virtual_ip ?? '' },
    { key: 'public_ip', header: t('IP pubblico'), mono: true, value: (r) => r.public_ip ?? '' },
    { key: 'login_time', header: t('Collegato da'), value: (r) => r.login_time ?? '' },
  ]
  const colonnePc: Column<PresenceComputer>[] = [
    {
      key: 'name',
      header: t('Nome'),
      value: (r) => r.name ?? '',
      render: (r) => <Link to={`/cis/${r.ci_id}`}>{r.name ?? '—'}</Link>,
    },
    { key: 'class', header: t('Classe'), value: (r) => r.ci_class ?? '' },
    { key: 'user', header: t('Utente collegato'), value: (r) => r.logged_user },
    {
      key: 'seen',
      header: t('Ultimo rilevamento'),
      value: (r) => r.last_seen ?? '',
      render: (r) => (r.last_seen ? new Date(r.last_seen).toLocaleString(localeCorrente()) : '—'),
    },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{login || t('Presenza')}</h1>
          <p className="page__subtitle">
            {t('Dove risulta collegata questa persona, per sorgente: Wi-Fi e PC dall’ultimo inventario, VPN chiesta al firewall adesso.')}
          </p>
        </div>
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      </header>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : (
        <>
          <Card title={t('Wi-Fi')}>
            {d.wifi.length ? (
              <DataTable columns={colonneWifi} rows={d.wifi} rowKey={(r) => r.mac} />
            ) : (
              <EmptyState
                title={t('Nessun client Wi-Fi con questa login')}
                hint={t('Le login arrivano dalle reti a 802.1X/Portal, con l’inventario avanzato dei controller wireless.')}
              />
            )}
          </Card>
          <Card title={t('VPN')}>
            {d.vpn.sessions.length ? (
              <DataTable
                columns={colonneVpn}
                rows={d.vpn.sessions}
                rowKey={(r, i) => `${r.firewall}-${i}`}
              />
            ) : d.vpn.available ? (
              <EmptyState title={t('Nessuna sessione VPN attiva per questa login')} />
            ) : (
              <EmptyState
                title={t('Nessun firewall interrogabile')}
                hint={t('La VPN si legge in tempo reale dai firewall Palo Alto, FortiGate e WatchGuard configurati in Integrazioni.')}
              />
            )}
            {d.vpn.cached && (
              <p className="field__hint">
                {t('Il firewall non ha risposto adesso: sessioni dall’ultima sincronizzazione (al più 5 minuti fa).')}
              </p>
            )}
            {d.vpn.errors.length > 0 && (
              <p className="field__hint">
                {t('Firewall non raggiunti: {elenco}', { elenco: d.vpn.errors.join(' · ') })}
              </p>
            )}
          </Card>
          <Card title={t('PC')}>
            {d.computers.length ? (
              <DataTable columns={colonnePc} rows={d.computers} rowKey={(r) => r.ci_id} />
            ) : (
              <EmptyState
                title={t('Nessun PC dichiara questa login')}
                hint={t('Serve l’attributo «Utente collegato» dall’inventario degli host: arriverà con l’agent e con WinRM.')}
              />
            )}
          </Card>
        </>
      )}
    </>
  )
}
